import { useState } from "react";
import { useFormField } from "../../hooks/useFormField";
import { FieldHint } from "../FieldHint";
import { labelClass } from "./fieldStyles";
import type { IntakeFormData } from "../../types/form";
import type { useStepValidation } from "../../hooks/useStepValidation";

type SliderNumberField = {
  [K in keyof IntakeFormData]: IntakeFormData[K] extends number ? K : never;
}[keyof IntakeFormData];

interface SliderInputProps {
  field: SliderNumberField;
  label: string;
  validation: ReturnType<typeof useStepValidation>;
  min?: number;
  max?: number;
  suffix?: string;
  hint?: string;
  required?: boolean;
  containerClassName?: string;
}

/**
 * Dual-control slider: range input + editable text input that stay in sync.
 * Text input clamps to [min, max] on blur; pressing Enter commits immediately.
 * Integrates with step validation for inline error display.
 *
 * Usage:
 *   <SliderInput field="medicalContribution" label="Employer Medical Contribution"
 *     min={50} max={100} hint="Employee-only tier, least expensive plan."
 *     validation={validation} required />
 */
export function SliderInput({
  field,
  label,
  validation,
  min = 0,
  max = 100,
  suffix = "%",
  hint,
  required,
  containerClassName,
}: SliderInputProps) {
  const f = useFormField(field, validation);
  const currentValue = (f.value as number) ?? min;
  const [editText, setEditText] = useState(String(currentValue));
  const [editing, setEditing] = useState(false);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    setEditText(raw);
  };

  const handleTextBlur = () => {
    setEditing(false);
    const n = parseInt(editText, 10);
    const clamped = isNaN(n) ? min : Math.max(min, Math.min(max, n));
    f.setValue(clamped as IntakeFormData[typeof field]);
    setEditText(String(clamped));
    f.onBlur();
  };

  return (
    <div className={containerClassName}>
      <label className={labelClass()} htmlFor={`${field}-slider`}>
        {label}
        {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
      </label>
      {hint && <p className="text-xs text-gray-500 mb-1.5">{hint}</p>}
      <div className="flex items-center gap-3 mt-1">
        <input
          id={`${field}-slider`}
          type="range"
          min={min}
          max={max}
          value={currentValue}
          onChange={(e) =>
            f.setValue(Number(e.target.value) as IntakeFormData[typeof field])
          }
          onMouseUp={f.onBlur}
          onTouchEnd={f.onBlur}
          className="flex-1 h-2 cursor-pointer accent-brand-green"
          aria-valuetext={`${currentValue}${suffix}`}
        />
        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white shrink-0">
          <input
            type="text"
            inputMode="numeric"
            value={editing ? editText : String(currentValue)}
            onChange={handleTextChange}
            onFocus={() => { setEditing(true); setEditText(String(currentValue)); }}
            onBlur={handleTextBlur}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
            className="w-12 px-2 py-1.5 text-base sm:text-sm text-center focus:outline-none"
            aria-label={`${label} value`}
          />
          {suffix && (
            <span className="px-2 py-1.5 text-sm text-gray-500 bg-gray-50 border-l border-gray-300">
              {suffix}
            </span>
          )}
        </div>
      </div>
      <FieldHint error={f.error} isPrefilled={f.isPrefilled} mismatch={f.mismatch} />
    </div>
  );
}
